'use client';

import { useEffect, useRef, type RefObject } from 'react';
import type { LoqateFindItem } from '../types';

type UseScrollManagementProps = {
  suggestionsRef: RefObject<HTMLDivElement | null>;
  highlightedIndex: number;
  id: string;
  suggestions: LoqateFindItem[];
  open: boolean;
  setIsScrollable: (isScrollable: boolean) => void;
};

function useScrollManagement({ suggestionsRef, highlightedIndex, id, suggestions, open, setIsScrollable }: UseScrollManagementProps) {
  // Create a ref to track the previous scrollable state
  const prevScrollableRef = useRef(false);

  // Check if content is scrollable
  useEffect(() => {
    // Only run if the dropdown is open and we have a valid ref
    if (open && suggestionsRef.current) {
      const container = suggestionsRef.current;
      const isContentScrollable = container.scrollHeight > container.clientHeight;

      // Only update if the scrollable state has changed
      if (isContentScrollable !== prevScrollableRef.current) {
        setIsScrollable(isContentScrollable);
        prevScrollableRef.current = isContentScrollable;
      }
    }
  }, [suggestions, open, setIsScrollable, suggestionsRef]);

  // Scroll highlighted item into view
  useEffect(() => {
    // Only run if we have a highlighted item and the dropdown is open
    if (highlightedIndex >= 0 && open && suggestionsRef.current) {
      const container = suggestionsRef.current;
      const item = container.querySelector(`#${id}-suggestion-${highlightedIndex}`);

      if (item) {
        const containerRect = container.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();

        // Check if item is outside the visible area of the container
        if (itemRect.bottom > containerRect.bottom) {
          // Item is below the visible area
          container.scrollTop = container.scrollTop + (itemRect.bottom - containerRect.bottom);
        } else if (itemRect.top < containerRect.top) {
          // Item is above the visible area
          container.scrollTop = container.scrollTop - (containerRect.top - itemRect.top);
        }
      }
    }
  }, [highlightedIndex, id, suggestionsRef, open]);
}

export { useScrollManagement };
